<template>
  <div class="experiment-list">
    <div class="header-container">
      <div class="title-wrap">
        <h3>Experiments</h3>
        <span class="meta-count">{{ (experiments || []).length }} item(s)</span>
      </div>
      <div class="action-buttons hide-on-collapse">
        <button @click="navigateToCreateExperiment">Create Experiment</button>
        <button @click="navigateToCreateSeriesFromAction">Create Series</button>
      </div>
    </div>

    <div class="search-filter-row">
      <input type="text" v-model="searchQuery" placeholder="Search by Name..." class="search-input">
      <button @click="showFilters = !showFilters" class="toggle-filter-button">
        {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
      </button>
    </div>

    <!-- Collapsible Filter Menu -->
    <div v-if="showFilters" class="filters">
      <input type="date" v-model="startDate" class="date-input">
      <input type="date" v-model="endDate" class="date-input">
    </div>

    <!-- Experiment Table -->
    <table class="experiment-table">
      <thead>
        <tr>
          <th @click="sortExperiments('name')">Name<i v-if="currentSortColumn === 'name'" class="filter-arrow" :class=(setArrowdirection(currentSortDirection))></i></th>
          <th @click="sortExperiments('start_date')">Start Date<i v-if="currentSortColumn === 'start_date'" class="filter-arrow" :class=(setArrowdirection(currentSortDirection))></i></th>
          <th @click="sortExperiments('end_date')">End Date<i v-if="currentSortColumn === 'end_date'" class="filter-arrow" :class=(setArrowdirection(currentSortDirection))></i></th>           
        </tr>
      </thead>
      <tbody>
        <tr v-for="experiment in paginatedExperiments" :key="experiment.id" @click="navigateToExperimentDetail(experiment.id)" class="experiment-row">
          <td>{{ experiment.name }}</td>
          <td>{{ formatDate(experiment.start_date) }}</td>
          <td>{{ formatDate(experiment.end_date) }}</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button v-for="page in visiblePageNumbers" :key="page" @click="setPage(page)" :class="{ active: page === currentPage }">{{ page }}</button>
    </div>
  </div>
</template>

  
  <script>
  import { API_ENDPOINTS } from '@/services/config';
  import apiService from '@/services/apiService';
  import { mapState } from 'vuex';
  
  export default {
  props: {
    experiments: {
      type: Array,
      required: true
    },
    actionId: {
      type: Number,
      required: false
    }, 
    actionName: {
      type: String,
      required: false
    },
    locationId: {
      type: Number,
      required: false
    },
    projectId: {
      type: Number,
      required: false
    },
    projectName: {
      type: String,
      required: false
    },
    workflowId: {
      type: Number,
      required: false
    },
    workflowName: {
      type: String,
      required: false
    },
    structureElementId: {
      type: Number,
      required: false,
      default: null,
    },
    useCreateExperimentModal: {
      type: Boolean,
      default: false,
    },
  },
    data() {
      return {
        searchQuery: '',
        currentPage: 1,
        experimentsPerPage: 5,
        currentSortColumn: 'name',
        currentSortDirection: 'asc',
        showFilters: false,
        startDate: '',
        endDate: '',
        sortedExperiments: [], // Local copy of experiments for sorting
      };
    },
    watch: {
      experiments: {
        handler(newVal) {
          if (!newVal || !Array.isArray(newVal)) return; // Wait until experiments is loaded
          this.sortedExperiments = [...newVal];
          this.sortExperiments(this.currentSortColumn);
        },
        immediate: true
      }
    },
    methods: {
      async navigateToCreateSeriesFromAction() {
        // If we have an actionId, fetch action info; otherwise use props directly
        if (this.actionId) {
          console.log(this.actionId)
          const activityData = await apiService.fetchResources(API_ENDPOINTS.actionInfo(this.actionId), this.loggedInUser.entra_id);
          this.$router.push({
              name: "CreateSeries",
              query: {
                returnTo: this.$route.fullPath,
                returnToName: this.$route.name,
                returnToParams: JSON.stringify(this.$route.params || {}),
                returnToQuery: JSON.stringify(this.$route.query || {}),
                  actionId: this.actionId,
                  actionName: this.actionName,
                  projectName: activityData.project_name,
                  workflowName: activityData.workflow_name,
                  projectId: activityData.project_id,
                  workflowId: activityData.workflow_id
              }
          });
        } else {
          // Navigate with available project info
          this.$router.push({
              name: "CreateSeries",
              query: {
                returnTo: this.$route.fullPath,
                returnToName: this.$route.name,
                returnToParams: JSON.stringify(this.$route.params || {}),
                returnToQuery: JSON.stringify(this.$route.query || {}),
                  projectId: this.projectId,
                  projectName: this.projectName,
                  workflowId: this.workflowId,
                  workflowName: this.workflowName
              }
          });
        }
    },
      async navigateToCreateExperiment() {
        if (this.useCreateExperimentModal) {
          this.$emit('create-experiment');
          return;
        }

        this.$router.push({ name: 'CreateExperiment', 
          query: { 
            projectId: this.projectId,
            projectName: this.projectName,
            location_id: this.locationId,
            project_structure_element_id: this.structureElementId,
          } 
        });
      },
      fetchExperiments() {
      console.log("Fetching experiments");
    },
      sortExperiments(column) {
        if (this.currentSortColumn === column) {
          this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          this.currentSortDirection = 'asc';
          this.currentSortColumn = column;
        }
  
        this.sortedExperiments.sort((a, b) => {
          if (a[column] < b[column]) return this.currentSortDirection === 'asc' ? -1 : 1;
          if (a[column] > b[column]) return this.currentSortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      },
  
      setPage(pageNumber) {
        this.currentPage = pageNumber;
      },

      setArrowdirection(sortDirection){
        if (sortDirection === 'desc'){
         return "bi bi-arrow-down"
        }
        return "bi bi-arrow-up"
      },
  
      navigateToExperimentDetail(experimentId) {
        this.$router.push({ name: 'ExperimentDetailPage', params: { id: experimentId } });
      },

      formatDate(dateString) {
        if (!dateString) {
        return "N/A";
        }
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
      },
    },
  
    computed: {
      ...mapState(['loggedInUser']),
      filteredExperiments() {
        return this.sortedExperiments.filter(experiment => {
          const name = experiment.name?.toLowerCase() || '';
  
          const matchesSearchQuery = name.includes(this.searchQuery.toLowerCase());
  
          const matchesStartDate = this.startDate === '' || new Date(experiment.start_date) >= new Date(this.startDate);
          const matchesEndDate = this.endDate === '' || new Date(experiment.end_date) <= new Date(this.endDate);
  
          return matchesSearchQuery && matchesStartDate && matchesEndDate;
        });
      },
      paginatedExperiments() {
        const start = (this.currentPage - 1) * this.experimentsPerPage;
        const end = start + this.experimentsPerPage;
        return this.filteredExperiments.slice(start, end);
      },
      totalPages() {
        return Math.ceil(this.filteredExperiments.length / this.experimentsPerPage);
      },
      visiblePageNumbers() {
        const range = 15; 
        let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
        let endPage = startPage + range - 1;
  
        if (endPage > this.totalPages) {
          endPage = this.totalPages;
          startPage = Math.max(1, endPage - range + 1);
        }
  
        return Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);
      }
    },
  };
  </script>
  
  <style scoped lang="scss">
  @import "@/assets/variables.scss";
  
  .experiment-list {
    max-width: 800px;
    margin: 20px auto;
    padding: 20px;
    background: $secondary-color;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
    h3 {
      margin: 0;
      color: $primary-color;
    }

    .search-filter-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 8px 0;
    }

    .search-input {
      flex: 1;
      margin: 0;
    }

    .toggle-filter-button {
      width: auto;
      margin: 0;
      padding: 6px 10px;
      white-space: nowrap;
    }

    .experiment-table {
        width: 100%;
    }

    .experiment-table th{
      background-color: $form-color;
      border: 1px solid $primary-color;
      cursor: pointer;
    }

    .filters {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: none;
    margin: 6px 0;
    gap: 8px;
  }

  .experiment-row{
    background-color: $secondary-color;
    cursor: pointer;
  }

  .experiment-row:hover{
    background-color: $form-color;
  }

    .pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }

  .pagination button {
    margin: 0 5px;
    padding: 5px 10px;
    background-color: $primary-color;
    border: 1px solid $secondary-color;
    border-radius: 5px;
    cursor: pointer;
  }

  .pagination button:hover,
  .pagination button.active {
    background-color: darken($primary-color, 10%);
  }

  .header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 58px;
  margin-bottom: 26px;
}

.title-wrap {
  display: flex;
  align-items: baseline;
  gap: 14px;
}

.meta-count {
  font-size: 0.92rem;
  font-weight: 500;
  color: rgba($primary-color, 0.8);
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.action-buttons button {
  background-color: $primary-color;
  color: white;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 5px;
}

.action-buttons button:hover {
  background-color: darken($primary-color, 10%);
}


}
  </style>
  